<script setup lang="ts">
import { computed, nextTick, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import type { Todo, TodoPatch } from '@/api'
import ActionsMenu, { type MenuItem } from '@/components/todos/ActionsMenu.vue'
import AlarmEditor from '@/components/todos/AlarmEditor.vue'
import AttachmentList from '@/components/todos/AttachmentList.vue'
import AttachmentMedia from '@/components/todos/AttachmentMedia.vue'
import CommentList from '@/components/todos/CommentList.vue'
import DateTimeField from '@/components/todos/DateTimeField.vue'
import MarkdownView from '@/components/todos/MarkdownView.vue'
import QuickAdd from '@/components/todos/QuickAdd.vue'
import RRuleEditor from '@/components/todos/RRuleEditor.vue'
import TagInput from '@/components/todos/TagInput.vue'
import TodoCheckbox from '@/components/todos/TodoCheckbox.vue'
import TodoTree from '@/components/todos/TodoTree.vue'
import { useBackButton } from '@/composables/useBackButton'
import { goBack, onBackClick } from '@/router/goBack'
import { useTags } from '@/composables/useTags'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { useTodosStore } from '@/stores/todos'
import { chooseDialog, confirmDialog, hapticSuccess, openExternalLink } from '@/telegramWebApp'
import { formatTemporalLong, isOverdue } from '@/todos/dates'
import { buildListView, isDone, reorderSiblings } from '@/todos/listView'
import { priorityLabel, PRIORITY_VALUES, priorityLevel, type PriorityLevel } from '@/todos/priority'
import { tagNames } from '@/todos/tags'
import { assigneeLink, todoAsText } from '@/todos/text'
import { descendantUids, type TodoNode } from '@/todos/tree'
import { TODO_TREE_CONTEXT } from '@/todos/treeState'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useTodosStore()
const { tags } = useTags()
const toast = useToast()

const calendarId = computed(() => Number(route.params.calendarId))
const uid = computed(() => String(route.params.uid))
const calendar = computed(() => store.calendarById(calendarId.value))
const readonly = computed(() => calendar.value?.readonly ?? false)
const todos = computed(() => store.todosOf(calendarId.value))
const todo = computed(() => store.findTodo(calendarId.value, uid.value))
const loading = computed(() => store.loading[calendarId.value] ?? false)
const saving = ref(0)
const commenting = ref(false)

const tree = computed(() =>
  buildListView(todos.value, { status: 'all', search: '', sort: 'manual' }),
)
function findNode(nodes: TodoNode[], target: string): TodoNode | null {
  for (const node of nodes) {
    if (node.todo.uid === target) return node
    const found = findNode(node.children, target)
    if (found) return found
  }
  return null
}
const node = computed(() => findNode(tree.value, uid.value))
const subtasks = computed(() => node.value?.children ?? [])
const parent = computed(() =>
  todo.value?.parent_uid ? store.findTodo(calendarId.value, todo.value.parent_uid) : undefined,
)
const descendants = computed(() => descendantUids(todos.value, uid.value))
const parentCandidates = computed(() => {
  const excluded = new Set([uid.value, ...descendants.value])
  return todos.value
    .filter((t) => !excluded.has(t.uid) && !t.uid.startsWith('pending-'))
    .sort((a, b) => a.summary.localeCompare(b.summary))
})
const hasDate = computed(() => !!(todo.value?.due || todo.value?.start))

// Text fields keep a local draft while focused, so a live update arriving mid-typing doesn't
// clobber what the user is writing; they're saved on blur.
type TextField = 'summary' | 'description' | 'location' | 'url'
const drafts = reactive<Record<TextField, string>>({
  summary: '',
  description: '',
  location: '',
  url: '',
})
const focused = ref<TextField | null>(null)
watch(
  todo,
  (value) => {
    if (!value) return
    for (const field of Object.keys(drafts) as TextField[]) {
      if (focused.value !== field) drafts[field] = value[field] ?? ''
    }
  },
  { immediate: true },
)

async function save(changes: TodoPatch): Promise<void> {
  if (!todo.value || readonly.value) return
  saving.value++
  try {
    await store.update(calendarId.value, uid.value, changes)
  } catch (e) {
    reportError(e, 'detail.saveFailed')
    toast.error(e, t('detail.saveFailed'))
  } finally {
    saving.value--
  }
}

// The notes are shown rendered; a tap swaps in the textarea (focused = 'description' keeps it while typing).
const notesInput = ref<HTMLTextAreaElement | null>(null)
const showRenderedNotes = computed(
  () => focused.value !== 'description' && drafts.description.trim() !== '',
)

function editNotes(): void {
  if (readonly.value) return
  focused.value = 'description'
  void nextTick(() => notesInput.value?.focus())
}

function saveText(field: TextField): void {
  focused.value = null
  if (!todo.value) return
  const value = drafts[field].trim()
  if (field === 'summary' && !value) {
    drafts.summary = todo.value.summary
    return
  }
  if (value === (todo.value[field] ?? '')) return
  void save({ [field]: value || null })
}

async function setDone(done: boolean): Promise<void> {
  if (!todo.value) return
  const repeating = !!todo.value.rrule
  saving.value++
  try {
    const saved = await store.setDone(calendarId.value, uid.value, done)
    if (done) hapticSuccess()
    if (done && repeating && !isDone(saved)) toast.info(t('detail.repeatsMoved'))
  } catch (e) {
    reportError(e, 'detail.updateFailed')
    toast.error(e, t('detail.updateFailed'))
  } finally {
    saving.value--
  }
}

function setStatus(value: string): void {
  if (value === 'COMPLETED') void setDone(true)
  else void save({ status: (value || null) as Todo['status'] })
}

function setPriority(level: PriorityLevel): void {
  void save({ priority: PRIORITY_VALUES[level] })
}

async function addComment(text: string): Promise<void> {
  commenting.value = true
  try {
    await store.addComment(calendarId.value, uid.value, text)
  } catch (e) {
    reportError(e, 'detail.commentFailed')
    toast.error(e, t('detail.commentFailed'))
  } finally {
    commenting.value = false
  }
}

async function addSubtask(summary: string): Promise<void> {
  try {
    const maxOrder = Math.max(0, ...subtasks.value.map((n) => n.todo.sort_order ?? 0))
    await store.create(calendarId.value, {
      summary,
      parent_uid: uid.value,
      sort_order: maxOrder + 1,
    })
  } catch (e) {
    reportError(e, 'detail.subtaskFailed')
    toast.error(e, t('detail.subtaskFailed'))
  }
}

const showMoveSheet = ref(false)
const moveTargets = computed(() =>
  store.calendars.filter((c) => c.id !== calendarId.value && !c.readonly),
)
const menuItems = computed<MenuItem[]>(() => [
  { id: 'duplicate', label: t('detail.menu.duplicate'), disabled: readonly.value },
  {
    id: 'move',
    label: t('detail.menu.move'),
    disabled: readonly.value || !moveTargets.value.length,
  },
  { id: 'copy', label: t('detail.menu.copy') },
  { id: 'delete', label: t('detail.menu.delete'), danger: true, disabled: readonly.value },
])

async function onMenu(id: string): Promise<void> {
  if (!todo.value) return
  try {
    if (id === 'duplicate') {
      const copy = await store.duplicate(calendarId.value, uid.value)
      toast.info(t('detail.duplicated'))
      await router.push(`/todos/${calendarId.value}/${encodeURIComponent(copy.uid)}`)
    } else if (id === 'move') {
      showMoveSheet.value = true
    } else if (id === 'copy') {
      await navigator.clipboard.writeText(
        todoAsText(node.value ?? { todo: todo.value, children: [] }),
      )
      toast.info(t('detail.copied'))
    } else if (id === 'delete') {
      await remove()
    }
  } catch (e) {
    reportError(e, 'detail.didntWork')
    toast.error(e, t('detail.didntWork'))
  }
}

async function remove(): Promise<void> {
  const count = descendants.value.length
  let mode: 'delete' | 'orphan' | null
  if (count) {
    mode = (await chooseDialog(
      t('detail.deleteWithSubtasks', { name: todo.value?.summary, count }, count),
      [
        { id: 'delete', text: t('detail.deleteAll', { count: count + 1 }), destructive: true },
        { id: 'orphan', text: t('detail.keepSubtasks') },
      ],
    )) as 'delete' | 'orphan' | null
  } else {
    mode = (await confirmDialog(t('detail.deleteConfirm', { name: todo.value?.summary })))
      ? 'delete'
      : null
  }
  if (!mode) return
  const back = `/todos/${calendarId.value}`
  await store.remove(calendarId.value, uid.value, mode)
  await router.replace(back)
}

async function moveTo(targetId: number): Promise<void> {
  showMoveSheet.value = false
  try {
    await store.move(calendarId.value, uid.value, targetId)
    toast.info(
      t('detail.movedTo', { name: store.calendarById(targetId)?.name ?? t('detail.otherList') }),
    )
    await router.replace(`/todos/${targetId}/${encodeURIComponent(uid.value)}`)
  } catch (e) {
    reportError(e, 'detail.moveFailed')
    toast.error(e, t('detail.moveFailed'))
  }
}

provide(TODO_TREE_CONTEXT, {
  calendarId,
  readonly,
  collapsed: ref(new Set<string>()),
  toggleCollapsed() {},
  toggleDone(childUid) {
    const child = store.findTodo(calendarId.value, childUid)
    if (child)
      store.setDone(calendarId.value, childUid, !isDone(child)).catch((e) => {
        reportError(e, 'detail.subtaskUpdateFailed')
        reportError(e, 'detail.subtaskUpdateFailed')
        toast.error(e, t('detail.subtaskUpdateFailed'))
      })
  },
  openTag(tag) {
    void router.push({ name: 'todo-tag', params: { calendarId: calendarId.value, tag } })
  },
  open(childUid) {
    void router.push(`/todos/${calendarId.value}/${encodeURIComponent(childUid)}`)
  },
  addSubtask(childUid) {
    void router.push(`/todos/${calendarId.value}/${encodeURIComponent(childUid)}`)
  },
  reorder(parentUid, movedUid, newIndex) {
    const siblings = (
      parentUid === uid.value
        ? subtasks.value
        : (findNode(tree.value, parentUid ?? '')?.children ?? [])
    ).map((n) => n.todo)
    for (const change of reorderSiblings(siblings, movedUid, newIndex)) {
      store.update(calendarId.value, change.uid, { sort_order: change.sort_order }).catch((e) => {
        reportError(e, 'detail.reorderFailed')
        reportError(e, 'detail.reorderFailed')
        toast.error(e, t('detail.reorderFailed'))
      })
    }
  },
})

watch(
  calendarId,
  (id, _old, onCleanup) => {
    if (!store.calendarsLoaded) void store.loadCalendars().catch(() => {})
    if (!store.todosByCalendar[id]) void store.loadTodos(id)
    onCleanup(store.watch(id))
  },
  { immediate: true },
)

const backTarget = computed(() =>
  parent.value
    ? `/todos/${calendarId.value}/${encodeURIComponent(parent.value.uid)}`
    : `/todos/${calendarId.value}`,
)
useBackButton(() => goBack(router, backTarget.value))
</script>

<template>
  <section class="detail-page">
    <header class="page-header">
      <RouterLink
        :to="`/todos/${calendarId}`"
        class="back"
        :aria-label="t('detail.backToList')"
        @click="onBackClick($event, router, `/todos/${calendarId}`)"
        >‹</RouterLink
      >
      <span class="ui-hint crumb"
        >{{ calendar?.name }}<template v-if="saving">{{ t('detail.saving') }}</template></span
      >
      <template v-if="todo">
        <button
          type="button"
          class="ui-btn ui-btn-icon flag"
          :class="{ on: todo.flagged }"
          :aria-pressed="todo.flagged"
          :disabled="readonly"
          :aria-label="t('detail.flag')"
          @click="save({ flagged: !todo.flagged })"
        >
          {{ todo.flagged ? '★' : '☆' }}
        </button>
        <ActionsMenu :items="menuItems" @select="onMenu" />
      </template>
    </header>

    <p v-if="!todo && loading" class="ui-hint">{{ t('common.loading') }}</p>
    <div v-else-if="!todo" class="missing">
      <p>{{ t('detail.missing') }}</p>
      <RouterLink :to="`/todos/${calendarId}`">{{ t('detail.backToTheList') }}</RouterLink>
    </div>

    <template v-else>
      <RouterLink
        v-if="parent"
        :to="`/todos/${calendarId}/${encodeURIComponent(parent.uid)}`"
        class="parent-link"
      >
        {{ t('detail.subtaskOf', { name: parent.summary }) }}
      </RouterLink>

      <div class="title-row">
        <TodoCheckbox :status="todo.status" :disabled="readonly" @toggle="setDone(!isDone(todo))" />
        <textarea
          v-model="drafts.summary"
          class="title"
          :class="{ done: isDone(todo) }"
          rows="1"
          :readonly="readonly"
          maxlength="500"
          @focus="focused = 'summary'"
          @blur="saveText('summary')"
          @keydown.enter.prevent="($event.target as HTMLTextAreaElement).blur()"
        />
      </div>

      <UiGroup :title="t('detail.section.status')">
        <UiList>
          <UiRow :label="t('detail.field.status')" as="label">
            <select
              class="ui-select"
              :value="todo.status ?? ''"
              :disabled="readonly"
              @change="setStatus(($event.target as HTMLSelectElement).value)"
            >
              <option value="">{{ t('detail.notSpecified') }}</option>
              <option value="NEEDS-ACTION">{{ t('detail.status.needsAction') }}</option>
              <option value="IN-PROCESS">{{ t('detail.status.inProcess') }}</option>
              <option value="COMPLETED">{{ t('detail.status.completed') }}</option>
              <option value="CANCELLED">{{ t('detail.status.cancelled') }}</option>
            </select>
          </UiRow>
          <UiRow :label="t('detail.field.completed')" v-if="todo.completed_at">
            <span>{{ formatTemporalLong(todo.completed_at) }}</span>
          </UiRow>
          <UiRow :label="t('detail.field.progress')" as="label">
            <span class="progress">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                :value="todo.percent_complete ?? 0"
                :disabled="readonly"
                @change="
                  save({
                    percent_complete: Number(($event.target as HTMLInputElement).value) || null,
                  })
                "
              />
              <span class="ui-hint">{{ todo.percent_complete ?? 0 }}%</span>
            </span>
          </UiRow>
        </UiList>
      </UiGroup>

      <UiGroup :title="t('detail.section.dates')">
        <UiList>
          <UiRow :label="t('detail.field.start')">
            <DateTimeField
              :model-value="todo.start"
              :disabled="readonly"
              @update:model-value="save({ start: $event })"
            />
          </UiRow>
          <UiRow>
            <template #label>
              <span :class="{ overdue: isOverdue(todo) }">{{ t('detail.field.due') }}</span>
            </template>
            <DateTimeField
              :model-value="todo.due"
              :disabled="readonly"
              @update:model-value="save({ due: $event })"
            />
          </UiRow>
          <UiRow :label="t('detail.field.repeat')" stacked>
            <RRuleEditor
              :model-value="todo.rrule"
              :has-date="hasDate"
              :disabled="readonly"
              @update:model-value="save({ rrule: $event })"
            />
          </UiRow>
          <UiRow :label="t('detail.field.reminders')" stacked>
            <AlarmEditor
              :model-value="todo.alarms"
              :has-date="!!todo.due"
              :disabled="readonly"
              @update:model-value="save({ alarms: $event })"
            />
          </UiRow>
        </UiList>
      </UiGroup>

      <UiGroup :title="t('detail.section.details')">
        <UiList>
          <UiRow :label="t('detail.field.priority')" stack-narrow>
            <div
              class="ui-segmented priority"
              role="group"
              :aria-label="t('detail.field.priority')"
            >
              <button
                v-for="level in ['none', 'low', 'medium', 'high'] as PriorityLevel[]"
                :key="level"
                type="button"
                :aria-pressed="priorityLevel(todo.priority) === level"
                :disabled="readonly"
                @click="setPriority(level)"
              >
                {{ priorityLabel(level) }}
              </button>
            </div>
          </UiRow>
          <UiRow :label="t('detail.field.tags')">
            <TagInput
              prefix="# "
              category-slot
              :placeholder="t('detail.tagsPlaceholder')"
              :suggestions="tagNames(store.knownTags, tags)"
              :model-value="todo.categories"
              :disabled="readonly"
              @update:model-value="save({ categories: $event })"
            />
          </UiRow>
          <UiRow :label="t('detail.field.assignedTo')">
            <TagInput
              :placeholder="t('detail.assigneePlaceholder')"
              :model-value="todo.assignees"
              :link-for="assigneeLink"
              :disabled="readonly"
              @update:model-value="save({ assignees: $event })"
            />
          </UiRow>
          <UiRow :label="t('detail.field.location')" as="label">
            <input
              v-model="drafts.location"
              class="ui-input"
              :placeholder="t('detail.locationPlaceholder')"
              :readonly="readonly"
              @focus="focused = 'location'"
              @blur="saveText('location')"
              @keydown.enter="($event.target as HTMLInputElement).blur()"
            />
          </UiRow>
          <UiRow label="URL">
            <input
              v-model="drafts.url"
              class="ui-input"
              inputmode="url"
              placeholder="https://…"
              :readonly="readonly"
              @focus="focused = 'url'"
              @blur="saveText('url')"
              @keydown.enter="($event.target as HTMLInputElement).blur()"
            />
            <button
              v-if="todo.url"
              type="button"
              class="ui-btn ui-btn-icon"
              :aria-label="t('detail.openLink')"
              @click="openExternalLink(todo.url)"
            >
              ↗
            </button>
          </UiRow>
          <UiRow :label="t('detail.field.subtaskOf')" as="label">
            <select
              class="ui-select"
              :value="todo.parent_uid ?? ''"
              :disabled="readonly"
              @change="save({ parent_uid: ($event.target as HTMLSelectElement).value || null })"
            >
              <option value="">{{ t('detail.topLevel') }}</option>
              <option v-if="todo.parent_uid && !parent" :value="todo.parent_uid">
                {{ t('detail.otherListTodo') }}
              </option>
              <option
                v-for="candidate in parentCandidates"
                :key="candidate.uid"
                :value="candidate.uid"
              >
                {{ candidate.summary }}
              </option>
            </select>
          </UiRow>
          <UiRow :label="t('detail.field.privacy')" as="label">
            <select
              class="ui-select"
              :value="todo.privacy ?? ''"
              :disabled="readonly"
              @change="
                save({
                  privacy: (($event.target as HTMLSelectElement).value || null) as Todo['privacy'],
                })
              "
            >
              <option value="">{{ t('detail.notSpecified') }}</option>
              <option value="PUBLIC">{{ t('detail.privacy.public') }}</option>
              <option value="CONFIDENTIAL">{{ t('detail.privacy.confidential') }}</option>
              <option value="PRIVATE">{{ t('detail.privacy.private') }}</option>
            </select>
          </UiRow>
          <UiRow :label="t('detail.field.eventStatus')" as="label">
            <select
              class="ui-select"
              :value="todo.event_status ?? ''"
              :disabled="readonly"
              @change="
                save({
                  event_status: (($event.target as HTMLSelectElement).value ||
                    null) as Todo['event_status'],
                })
              "
            >
              <option value="">{{ t('detail.notSpecified') }}</option>
              <option value="TENTATIVE">{{ t('detail.event.tentative') }}</option>
              <option value="CONFIRMED">{{ t('detail.event.confirmed') }}</option>
              <option value="CANCELLED">{{ t('detail.status.cancelled') }}</option>
            </select>
          </UiRow>
          <UiRow :label="t('detail.field.showAs')" as="label">
            <select
              class="ui-select"
              :value="todo.show_as ?? ''"
              :disabled="readonly"
              @change="
                save({
                  show_as: (($event.target as HTMLSelectElement).value || null) as Todo['show_as'],
                })
              "
            >
              <option value="">{{ t('detail.notSpecified') }}</option>
              <option value="OPAQUE">{{ t('detail.showAs.busy') }}</option>
              <option value="TRANSPARENT">{{ t('detail.showAs.free') }}</option>
            </select>
          </UiRow>
        </UiList>
      </UiGroup>

      <UiGroup :title="t('detail.section.notes')">
        <MarkdownView
          v-if="showRenderedNotes"
          class="notes"
          :source="drafts.description"
          :role="readonly ? undefined : 'button'"
          :aria-label="readonly ? undefined : t('detail.notesEdit')"
          @edit="editNotes"
        />
        <textarea
          v-else
          ref="notesInput"
          v-model="drafts.description"
          class="ui-textarea notes"
          rows="4"
          :placeholder="t('detail.notesPlaceholder')"
          :readonly="readonly"
          @focus="focused = 'description'"
          @blur="saveText('description')"
        />
        <AttachmentMedia :todo="todo" :calendar-id="calendarId" />
      </UiGroup>

      <UiGroup :title="t('detail.section.subtasks')">
        <UiList v-if="subtasks.length" class="subtasks">
          <TodoTree :nodes="subtasks" :parent-uid="uid" :sortable="!readonly" :depth="0" />
        </UiList>
        <QuickAdd
          v-if="!readonly"
          :placeholder="t('detail.subtaskPlaceholder')"
          @add="addSubtask"
        />
      </UiGroup>

      <UiGroup :title="t('detail.section.attachments')">
        <UiList class="padded">
          <AttachmentList :todo="todo" :calendar-id="calendarId" :readonly="readonly" />
        </UiList>
      </UiGroup>

      <UiGroup :title="t('detail.section.comments')">
        <CommentList
          :comments="todo.comments"
          :readonly="readonly"
          :busy="commenting"
          @add="addComment"
        />
      </UiGroup>

      <p class="ui-hint footer">
        <template v-if="todo.created"
          >{{ t('detail.created', { date: formatTemporalLong(todo.created) }) }}
        </template>
        <template v-if="todo.last_modified">{{
          t('detail.lastChanged', { date: formatTemporalLong(todo.last_modified) })
        }}</template>
      </p>
    </template>

    <div v-if="showMoveSheet" class="ui-backdrop" @click.self="showMoveSheet = false">
      <div class="ui-sheet">
        <i18n-t
          :keypath="descendants.length ? 'detail.moveTitleWithSubtasks' : 'detail.moveTitle'"
          tag="p"
          class="sheet-title"
          :plural="descendants.length"
        >
          <template #name
            ><strong>{{ todo?.summary }}</strong></template
          >
          <template #count>{{ descendants.length }}</template>
        </i18n-t>
        <UiGroup>
          <UiList>
            <UiRow
              as="button"
              v-for="target in moveTargets"
              :key="target.id"
              type="button"
              class="move-target"
              @click="moveTo(target.id)"
            >
              <span>{{ target.name }}</span>
              <span class="ui-hint">{{ target.account_name }}</span>
            </UiRow>
          </UiList>
        </UiGroup>
        <button type="button" class="ui-btn cancel" @click="showMoveSheet = false">
          {{ t('common.cancel') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}

.back {
  font-size: 1.8rem;
  line-height: 1;
  text-decoration: none;
}

.crumb {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flag.on {
  color: #ff9500;
}

.parent-link {
  display: inline-block;
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
  text-decoration: none;
}

.title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}

.title-row .ui-checkbox {
  margin-top: 0.35rem;
}

.title {
  flex: 1;
  min-height: 2.2rem;
  padding: 0.1rem 0;
  border: none;
  background: transparent;
  color: var(--tg-text-color);
  font: inherit;
  font-size: 1.3rem;
  font-weight: 600;
  resize: none;
  field-sizing: content;
  outline: none;
}

.title.done {
  color: var(--tg-hint-color);
  text-decoration: line-through;
}

.overdue {
  color: var(--tg-destructive-text-color);
}

.progress {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.progress input {
  flex: 1;
}

.priority button {
  flex: 1;
}

.notes {
  margin-bottom: 0.5rem;
}

.padded {
  padding: 0.3rem 0.85rem;
}

.footer {
  margin-top: 1.5rem;
}

.missing p {
  margin: 0.5rem 0;
}

.sheet-title {
  margin: 0 0 0.6rem;
}

.move-target {
  width: 100%;
  border: none;
  background: none;
  color: var(--tg-text-color);
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.cancel {
  width: 100%;
}
</style>
